import './excess-tower-layer-participant-detail.scss';

import {
  Alert,
  Badge,
  Box,
  IconButton,
  Link,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Circle,
  DeleteOutlined,
  ExpandLess,
  ExpandMore,
  Sync,
  WarningAmberRounded,
} from '@mui/icons-material';
import {
  ConvergeTooltip,
  FormToggleSwitchField,
  NewFormTextField,
} from '../../../../presentation/components';
import {
  FocusEvent,
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  IUnderwritingQuoteExcessLayerParticipant,
  IUnderwritingQuoteExcessTowerLayer,
  getDisplayNameFromEntity,
  useDeleteExcessTowerLayerParticipant,
} from '@converge/graphql';
import { InsuredEntity, InsurerSelectBox } from './insurer-select-box';
import { formatDollarAmount, formatPercent } from '@converge/utils';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { CELL_COUNT } from './rater.excess.view';
import { COVERAGE_TYPE_LABELS, numberFieldMaxValue } from '@converge/data';
import { CascadeLimitAndRetentionTowerButton } from '../coverage-rating/cascade-limit-and-retention-tower-button';
import { CurrencyOrNotApplicable } from './currency-or-not-applicable';
import { NumberField } from '../common/number.field';
import { PercentOrNotApplicable } from './percent-or-not-applicable';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import config from '@converge/config';
import { getParsedValue } from '../coverage-rating';
import {
  getEndorsementLimitDisplay,
  getPrimaryEndorsementsForExcess,
} from './primary-endorsements-carry-over';
import { hasErrors } from '../enterprise-rating';
import { isConverge } from '@converge/data';
import { useConvergeSnackBar } from '../../../../contexts';
import { useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  getSubCoverageGroupWarnings,
  SubCoverageGroupWarning,
} from './sub-coverage-group-validation';

interface RaterExcessTowerLayerParticipantProps {
  participant: IUnderwritingQuoteExcessLayerParticipant;
  layer: IUnderwritingQuoteExcessTowerLayer;
  isDisabled: boolean;
  layerLimit: number;
  isPrimaryLayer: boolean;
  excludeFromInsurerSelect: string[];
}

export const RaterExcessTowerLayerParticipant = ({
  participant,
  participant: { uuid, quote },
  layer,
  isDisabled,
  isPrimaryLayer,
  excludeFromInsurerSelect,
}: RaterExcessTowerLayerParticipantProps) => {
  const limit = quote?.limit ?? null;
  const policyReferenceId = quote?.policyReferenceId;

  const [showInsurerPicker, setShowInsurerPicker] = useState(false);

  const {
    isEditable,
    policy,
    quoteUuid,
    upsertQuoteIsLoading,
    expandCoverageListForExcessTowerLayerParticipants,
  } = useRaterStateContextSelector(
    ({
      isEditable,
      policy,
      quote: { uuid: quoteUuid },
      upsertQuoteIsLoading,
      expandCoverageListForExcessTowerLayerParticipants,
    }) => {
      return {
        isEditable,
        policy,
        quoteUuid,
        upsertQuoteIsLoading,
        expandCoverageListForExcessTowerLayerParticipants,
      };
    }
  );

  const { dispatch, actions } = useRaterActionDispatchContext();

  const excessTowerLayerParticipantDeleter =
    useDeleteExcessTowerLayerParticipant();

  const { notify } = useConvergeSnackBar();

  const expandCoveragesList = useMemo(() => {
    return expandCoverageListForExcessTowerLayerParticipants.some(
      (tuple) => tuple[0] === layer.uuid && tuple[1] === participant.uuid
    );
  }, [
    expandCoverageListForExcessTowerLayerParticipants,
    layer.uuid,
    participant.uuid,
  ]);

  useEffect(() => {
    if (excessTowerLayerParticipantDeleter.error) {
      notify({
        message: `Failed to delete participant: ${excessTowerLayerParticipantDeleter.error}`,
        variant: 'error',
        preventDuplicate: true,
      });
    }
  }, [excessTowerLayerParticipantDeleter.error, notify]);

  const notApplicableMessage = isPrimaryLayer
    ? 'Not applicable for primary layer'
    : 'Not applicable';

  const isConvergeEntity = isConverge(participant);

  useEffect(() => {
    if (
      isConvergeEntity &&
      policy &&
      policy.referenceId &&
      !participant.quote?.policyReferenceId
    ) {
      dispatch(
        actions.updateExcessTowerLayerParticipant(
          layer.uuid,
          participant.uuid,
          {
            policyReferenceId: policy.referenceId,
          }
        )
      );
    }
  }, [
    actions,
    dispatch,
    isConvergeEntity,
    layer.uuid,
    participant.quote?.policyReferenceId,
    participant.uuid,
    policy,
  ]);

  const handleInsurerSelectUpdate = useCallback(
    (broker: InsuredEntity | null) => {
      setShowInsurerPicker(false);

      if (broker) {
        dispatch(
          actions.replaceExcessTowerLayerParticipant(
            layer.uuid,
            participant.uuid,
            {
              ...broker,
              uuid: broker?.uuid ?? '',
              isNew: true,
              name: broker.name ?? getDisplayNameFromEntity(broker) ?? 'N/A',
              quote: { uuid: uuidv4(), limit: layer.limit },
            }
          )
        );
      }
    },
    [actions, dispatch, layer.limit, layer.uuid, participant.uuid]
  );

  const handlePolicyNumberBlur = useCallback(
    (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
      const newValue = event.target.value?.trim() ?? '';
      dispatch(
        actions.updateExcessTowerLayerParticipant(
          layer.uuid,
          participant.uuid,
          {
            policyReferenceId: newValue,
          }
        )
      );
    },
    [actions, dispatch, layer.uuid, participant.uuid]
  );

  const handleShowCoverageList = useCallback(() => {
    if (expandCoveragesList) {
      dispatch(
        actions.contractCoverageListForExcessTowerLayerParticipant(
          layer.uuid,
          participant.uuid
        )
      );
      return;
    }

    dispatch(
      actions.expandCoverageListForExcessTowerLayerParticipant(
        layer.uuid,
        participant.uuid
      )
    );
  }, [actions, dispatch, expandCoveragesList, layer.uuid, participant.uuid]);

  const handleRemoveParticipantClick = useCallback(() => {
    if (participant.isNew) {
      dispatch(
        actions.removeParticipantFromLayer(layer.uuid, participant.uuid)
      );
      return;
    }

    excessTowerLayerParticipantDeleter
      .deleteExcessTowerLayerParticipant({
        variables: {
          position: layer.position ?? -1,
          quoteUuid,
          participantUuid: participant.uuid,
        },
      })
      .then((data) => {
        dispatch(
          actions.removeParticipantFromLayer(layer.uuid, participant.uuid)
        );
      });
  }, [
    actions,
    dispatch,
    excessTowerLayerParticipantDeleter,
    layer.position,
    layer.uuid,
    participant.isNew,
    participant.uuid,
    quoteUuid,
  ]);
  const borderBottomWidth = expandCoveragesList ? 0 : undefined;

  const coveragesIncluded = participant.coverages.filter(
    (coverage) => coverage.quote.included
  );

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasInvalidSelectedLimit = hasErrors(
    `${participant.uuid}:${uuid}:limit`,
    errors
  );

  useEffect(() => {
    if (limit === 0) {
      setError(`${participant.uuid}:${uuid}:limit`, {
        type: `${participant.uuid}:${uuid}:limit`,
        message: `limit must be a greater than 0`,
      });
    }
  }, [limit, setError, uuid, participant]);
  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidSelectedLimit));
  }, [actions, dispatch, hasInvalidSelectedLimit]);

  const renderParticipantName = () => (
    <Typography>
      {config.featureFlags.enableRaterExcessLayerParticipantReplacement ? (
        <ConvergeTooltip
          title={isDisabled ? 'Insurer' : `Click to replace insurer`}
        >
          <Link
            onClick={(e) => {
              if (isDisabled) {
                return;
              }
              setShowInsurerPicker(true);
            }}
            sx={{
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              color: 'initial',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: isDisabled ? 'none' : 'underline',
              },
            }}
          >
            <Typography fontWeight={'bold'} fontSize="14px" display={'inline'}>
              {participant.name ?? participant.uuid.replace(/-.*/g, '')}
            </Typography>
          </Link>
        </ConvergeTooltip>
      ) : (
        <Typography fontWeight={'bold'} fontSize="14px" display={'inline'}>
          {participant.name ?? participant.uuid.replace(/-.*/g, '')}
        </Typography>
      )}
    </Typography>
  );

  return (
    <>
      <TableRow
        sx={{
          background:
            participant.hasChanged && !hasInvalidSelectedLimit
              ? 'rgb(253, 245, 231, 0.75)'
              : hasInvalidSelectedLimit
              ? 'rgba(219, 82, 76, 0.1)'
              : undefined,
        }}
      >
        {/* Name */}
        <TableCell
          className="tower-layer-participant-name-container"
          sx={{
            borderBottomWidth,
          }}
        >
          <Stack className="participant-name">
            {!showInsurerPicker ? (
              <>
                {renderParticipantName()}

                {participant.isNew && (
                  <Typography>
                    <ConvergeTooltip title="New">
                      <Circle className="circle" color="success" />
                    </ConvergeTooltip>
                  </Typography>
                )}
              </>
            ) : (
              <InsurerSelectBox
                name={`${layer.uuid}:insurerUuid:${participant.uuid}`}
                exclude={excludeFromInsurerSelect}
                insurerEntity={participant ?? undefined}
                onBlur={() => {
                  setShowInsurerPicker(false);
                }}
                clearOnBlur
                onChange={handleInsurerSelectUpdate}
              />
            )}
          </Stack>
        </TableCell>

        {/* Policy number */}
        <TableCell
          className="tower-layer-participant-policy-number-container"
          sx={{
            borderBottomWidth,
          }}
        >
          <TextField
            name={`${participant.uuid}:${uuid}:policyReferenceId`}
            defaultValue={policyReferenceId}
            disabled={isDisabled || isConvergeEntity}
            placeholder={policy?.referenceId ?? 'Policy #'}
            size="small"
            onBlur={handlePolicyNumberBlur}
          />
        </TableCell>

        {/* Limit */}
        <TableCell
          className="tower-layer-participant-limit-container"
          sx={{
            borderBottomWidth,
          }}
        >
          <Box minWidth={20}>
            {isPrimaryLayer ? (
              formatDollarAmount(limit)
            ) : (
              <NumberField
                isCurrency
                name={`${participant.uuid}:${uuid}:limit`}
                value={limit}
                max={numberFieldMaxValue}
                isDisabled={isDisabled}
                onBlur={(newValue) => {
                  const parsedValue = getParsedValue(newValue);
                  if (!parsedValue) {
                    setError(`${participant.uuid}:${uuid}:limit`, {
                      type: `${participant.uuid}:${uuid}:limit`,
                      message: `Limit must be a greater than 0`,
                    });
                    return;
                  }
                  dispatch(
                    actions.updateExcessTowerLayerParticipant(
                      layer.uuid,
                      participant.uuid,
                      {
                        limit: newValue || null,
                      }
                    )
                  );
                  clearErrors(`${participant.uuid}:${uuid}:limit`);
                }}
                error={hasInvalidSelectedLimit}
                hasChanged={layer.hasChanged}
              />
            )}
          </Box>
        </TableCell>

        {/* Quota share */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          {typeof participant.quote?.companyShare === 'number'
            ? formatPercent((participant.quote?.companyShare ?? 0) * 100)
            : 'N/A'}
        </TableCell>

        {/* Attachment point */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <CurrencyOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.attachmentPoint}
          </CurrencyOrNotApplicable>
        </TableCell>

        {/* Minimum premium */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <CurrencyOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.minimumPremium}
          </CurrencyOrNotApplicable>
        </TableCell>

        {/* Benchmark premium */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <CurrencyOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.benchmarkPremium}
          </CurrencyOrNotApplicable>
        </TableCell>

        {/* Bound premium */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <CurrencyOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.boundPremium}
          </CurrencyOrNotApplicable>
        </TableCell>

        {/* % of underlying */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <PercentOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.boundUnderlyingPercent}
          </PercentOrNotApplicable>
        </TableCell>

        {/* ILF - Increased limit factor */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <PercentOrNotApplicable tooltipText={notApplicableMessage}>
            {participant.quote?.boundIncreasedLimitFactor}
          </PercentOrNotApplicable>
        </TableCell>

        {/* Actions */}
        <TableCell
          sx={{
            borderBottomWidth,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            justifyContent={'end'}
            flexGrow={1}
          >
            {!isPrimaryLayer && (
              <IconButton
                onClick={handleShowCoverageList}
                disabled={upsertQuoteIsLoading}
              >
                {participant.isNew && upsertQuoteIsLoading ? (
                  <Sync className="spin" />
                ) : (
                  <Badge
                    badgeContent={coveragesIncluded.length}
                    showZero
                    color={
                      participant.coverages.length === 0 ? 'error' : 'primary'
                    }
                  >
                    {expandCoveragesList ? <ExpandLess /> : <ExpandMore />}
                  </Badge>
                )}
              </IconButton>
            )}

            {!isPrimaryLayer && isEditable && (
              <IconButton
                onClick={handleRemoveParticipantClick}
                disabled={
                  isDisabled || excessTowerLayerParticipantDeleter.loading
                }
              >
                {excessTowerLayerParticipantDeleter.loading ? (
                  <Sync className="spin" />
                ) : (
                  <DeleteOutlined />
                )}
              </IconButton>
            )}

            {/* <RaterNotesButton
              uuid={`${layer?.uuid}${participant.uuid}`}
              targetName={NoteTargetName.ExcessTowerLayerParticipant}
              targetType={NoteTargetType.Node}
            /> */}
          </Stack>
        </TableCell>
      </TableRow>

      {!isPrimaryLayer && (
        <TableRow>
          <TableCell
            colSpan={CELL_COUNT}
            sx={{
              padding: 0,
              borderBottom: 0,
            }}
          >
            <Stack
              sx={{
                overflow: 'hidden',
              }}
            >
              <Coverages
                layerUuid={layer.uuid}
                participant={participant}
                show={expandCoveragesList}
              />
            </Stack>
          </TableCell>
        </TableRow>
      )}

      {/* <RaterNotesRow
        uuid={layer.uuid}
        targetName={NoteTargetName.ExcessTowerLayerParticipant}
        targetType={NoteTargetType.Node}
        colSpan={CELL_COUNT}
      /> */}
    </>
  );
};

interface ICoveragesProps {
  participant: IUnderwritingQuoteExcessLayerParticipant;
  layerUuid: string;
  show: boolean;
}

const getTypeLabel = (label: string) => {
  return COVERAGE_TYPE_LABELS[label] ?? label;
};

const SubCoverageGroupWarningIcon = ({
  warning,
}: {
  warning: SubCoverageGroupWarning;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <ConvergeTooltip
        title={`Grouped as "${warning.groupName}" — click for details`}
      >
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ p: 0, ml: 0.25 }}
        >
          <WarningAmberRounded color="warning" sx={{ fontSize: 16 }} />
        </IconButton>
      </ConvergeTooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 360 }}>
          <Typography fontWeight={700} fontSize={13} sx={{ mb: 1 }}>
            Group &ldquo;{warning.groupName}&rdquo; — sub-limits differ
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}>
                  Sub-coverage
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ fontWeight: 600, fontSize: 12, py: 0.5 }}
                >
                  Sub-limit
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {warning.members.map((m) => (
                <TableRow key={m.subCoverageUuid}>
                  <TableCell sx={{ fontSize: 12, py: 0.5 }}>{m.name}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 12,
                      py: 0.5,
                      fontWeight:
                        m.subCoverageUuid === warning.subCoverageUuid
                          ? 700
                          : 400,
                    }}
                  >
                    {m.subLimit != null
                      ? formatDollarAmount(m.subLimit)
                      : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Popover>
    </>
  );
};

const Coverages = forwardRef<HTMLDivElement, ICoveragesProps>(
  (
    {
      participant: { coverages = [], quote },
      participant,
      layerUuid,
      show,
    }: ICoveragesProps,
    ref
  ) => {
    const participantQuoteLimit = quote?.limit || undefined;
    const { dispatch, actions } = useRaterActionDispatchContext();

    const {
      formState: { errors },
      setError,
      clearErrors,
    } = useFormContext();

    const { isEditable, quoteEndorsements } = useRaterStateContextSelector(
      ({ isEditable, quote: { endorsements } }) => ({
        isEditable,
        quoteEndorsements: endorsements,
      })
    );

    const primaryEndorsementsForExcess = useMemo(
      () =>
        getPrimaryEndorsementsForExcess(
          (quoteEndorsements ?? []).map((e) => ({
            primary: e.primary,
            included: e.quote?.included,
            limit: e.quote?.limit,
            nonPremiumBearing: e.nonPremiumBearing,
            name: e.name,
            uuid: e.uuid,
          }))
        ),
      [quoteEndorsements]
    );

    const subCoverageWarnings = useMemo(
      () => getSubCoverageGroupWarnings(coverages),
      [coverages]
    );

    const types: Record<string, boolean> = {};

    if (!show) {
      return null;
    }

    return (
      <Stack ref={ref} className="coverage-table-in-tower-header">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className="coverage-name-cell">
                <Typography fontWeight={600} fontSize="14px">
                  Coverage
                </Typography>
              </TableCell>
              <TableCell className="coverage-included-cell">
                <Typography fontWeight={600} fontSize="14px">
                  Included
                </Typography>
              </TableCell>
              <TableCell className="coverage-erosion-cell">
                <Typography fontWeight={600} fontSize="14px">
                  Erosion
                </Typography>
              </TableCell>
              <TableCell className="coverage-limit-cell">
                <Typography fontWeight={600} fontSize="14px">
                  Limit
                </Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600} fontSize="14px"></Typography>
              </TableCell>
              <TableCell>
                <Typography fontWeight={600} fontSize="14px">
                  Participant coverage name
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {coverages?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Alert color="error" icon={<PriorityHighIcon />}>
                    No coverages found for {participant.name}.
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              coverages.map((coverage) => {
                const { name, uuid, subCoverages, quote, type, hasChanged } =
                  coverage;

                const {
                  erosion = false,
                  participantCoverageName,
                  limit,
                  included,
                  uuid: coverageQuoteUuid,
                } = {
                  erosion: false,
                  included: false,
                  ...quote,
                };

                const prefix = `${participant.uuid}:${uuid}:${coverageQuoteUuid}`;
                const prefixLimit = `${prefix}:limit`;
                const row = (
                  <>
                    <TableRow
                      sx={{
                        backgroundColor: hasChanged
                          ? 'rgb(253, 245, 231, 0.75)'
                          : undefined,
                      }}
                    >
                      <TableCell
                        width={'120px'}
                        sx={{
                          paddingLeft: 4,
                          width: 120,
                        }}
                      >
                        <Typography fontWeight={600} fontSize="14px">
                          {name}
                        </Typography>
                      </TableCell>
                      <TableCell className="coverage-table-in-tower-toggle-body">
                        <FormToggleSwitchField.Switch
                          name={`${prefix}:included`}
                          checked={included ?? false}
                          disabled={!isEditable}
                          size="small"
                          onChange={(event, included) => {
                            dispatch(
                              actions.updateExcessTowerLayerParticipantCoverage(
                                layerUuid,
                                participant.uuid,
                                coverage.uuid,
                                {
                                  included,
                                }
                              )
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="coverage-table-in-tower-toggle-body">
                        <FormToggleSwitchField.Switch
                          name={`${prefix}:erosion`}
                          checked={erosion ?? false}
                          disabled={!included || !isEditable}
                          size="small"
                          onChange={(event, erosion) => {
                            dispatch(
                              actions.updateExcessTowerLayerParticipantCoverage(
                                layerUuid,
                                participant.uuid,
                                coverage.uuid,
                                {
                                  erosion,
                                }
                              )
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell className="coverage-table-in-tower-limit-body">
                        <Box minWidth={160}>
                          <NumberField
                            isCurrency
                            name={prefixLimit}
                            value={limit ?? null}
                            max={participantQuoteLimit}
                            isDisabled={!included || !isEditable}
                            error={
                              errors && errors[prefixLimit]
                                ? Object.prototype.hasOwnProperty.call(
                                    errors,
                                    prefixLimit
                                  )
                                : false
                            }
                            helperText={errors[prefixLimit]?.message as string}
                            onBlur={(newValue) => {
                              const parsed = getParsedValue(newValue);
                              if (
                                participantQuoteLimit &&
                                (!parsed || parsed > participantQuoteLimit)
                              ) {
                                setError(prefixLimit, {
                                  type: 'max',
                                  message: `Limit cannot exceed ${formatDollarAmount(
                                    participantQuoteLimit
                                  )}`,
                                });
                                return;
                              }
                              clearErrors(prefixLimit);
                              dispatch(
                                actions.updateExcessTowerLayerParticipantCoverage(
                                  layerUuid,
                                  participant.uuid,
                                  coverage.uuid,
                                  { limit: newValue }
                                )
                              );
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell width={'10px'}>
                        {isEditable && (
                          <CascadeLimitAndRetentionTowerButton
                            layerUuid={layerUuid}
                            participantUuid={participant.uuid}
                            limit={limit}
                            isFirstRow={false}
                            isLastRow={false}
                            isCyberCrime={coverage.name === 'Cyber Crime'}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <NewFormTextField
                          name={`${prefix}:participantCoverageName`}
                          value={participantCoverageName ?? ''}
                          size="small"
                          disabled={!included || !isEditable}
                          placeholder={`${participant.name} coverage name for ${name}`}
                          onBlur={(e) => {
                            dispatch(
                              actions.updateExcessTowerLayerParticipantCoverage(
                                layerUuid,
                                participant.uuid,
                                coverage.uuid,
                                {
                                  participantCoverageName:
                                    e?.currentTarget.value,
                                }
                              )
                            );
                          }}
                        />
                      </TableCell>
                    </TableRow>
                    {subCoverages.map((subCoverage) => {
                      const { hasChanged, uuid } = subCoverage;
                      const {
                        subLimit,
                        included = false,
                        erosion = false,
                        uuid: subCoverageQuoteUuid,
                      } = subCoverage.quote ?? {
                        subLimit: null,
                      };
                      const subCoveragePrefix = `${prefix}:${uuid}:${subCoverageQuoteUuid}`;
                      const groupWarning = subCoverageWarnings.get(
                        subCoverage.uuid ?? ''
                      );
                      return (
                        <TableRow
                          key={subCoverage.uuid}
                          sx={{
                            backgroundColor: hasChanged
                              ? 'rgb(253, 245, 231, 0.75)'
                              : undefined,
                          }}
                        >
                          <TableCell className="sub-coverage-name-header">
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography
                                fontWeight={600}
                                fontSize={'14px'}
                                sx={{
                                  opacity: !included ? 0.5 : undefined,
                                }}
                              >
                                {subCoverage.name}
                              </Typography>
                              {groupWarning && (
                                <SubCoverageGroupWarningIcon
                                  warning={groupWarning}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <FormToggleSwitchField.Switch
                              name={`${subCoveragePrefix}:included`}
                              checked={included ?? false}
                              size="small"
                              disabled={!coverage.quote.included || !isEditable}
                              onChange={(event, included) => {
                                dispatch(
                                  actions.updateExcessTowerLayerParticipantSubCoverage(
                                    layerUuid,
                                    participant.uuid,
                                    coverage.uuid,
                                    subCoverage.uuid ?? 'n/a',
                                    {
                                      included,
                                    }
                                  )
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <FormToggleSwitchField.Switch
                              name={`${subCoveragePrefix}:erosion`}
                              checked={erosion ?? false}
                              size="small"
                              disabled={
                                !coverage.quote?.included ||
                                !included ||
                                !isEditable
                              }
                              onChange={(event, erosion) => {
                                dispatch(
                                  actions.updateExcessTowerLayerParticipantSubCoverage(
                                    layerUuid,
                                    participant.uuid,
                                    coverage.uuid,
                                    subCoverage.uuid ?? 'n/a',
                                    {
                                      erosion,
                                    }
                                  )
                                );
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box className="sub-limit-header">
                              <NumberField
                                isCurrency
                                name={`${subCoveragePrefix}:subLimit`}
                                value={subLimit ?? null}
                                max={numberFieldMaxValue}
                                isDisabled={
                                  !coverage.quote?.included ||
                                  !included ||
                                  !isEditable
                                }
                                onBlur={(newValue) => {
                                  dispatch(
                                    actions.updateExcessTowerLayerParticipantSubCoverage(
                                      layerUuid,
                                      participant.uuid,
                                      coverage.uuid,
                                      subCoverage.uuid ?? 'n/a',
                                      {
                                        subLimit: newValue,
                                      }
                                    )
                                  );
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell width={'10px'}></TableCell>
                          <TableCell>
                            <NewFormTextField
                              name={`${subCoveragePrefix}:participantCoverageName`}
                              value={
                                subCoverage.quote?.participantCoverageName ?? ''
                              }
                              size="small"
                              placeholder={`${participant.name} coverage name for ${name}`}
                              disabled={
                                !coverage.quote?.included ||
                                !included ||
                                !isEditable
                              }
                              onBlur={(e) => {
                                dispatch(
                                  actions.updateExcessTowerLayerParticipantSubCoverage(
                                    layerUuid,
                                    participant.uuid,
                                    coverage.uuid,
                                    subCoverage.uuid ?? 'n/a',
                                    {
                                      participantCoverageName:
                                        e?.currentTarget.value,
                                    }
                                  )
                                );
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                );

                if (type && !types[type]) {
                  types[type] = true;
                  return (
                    <Fragment key={`header-and-row-${uuid}`}>
                      <TableRow className="main-header-converge-tables-tower-container ">
                        <TableCell
                          colSpan={12}
                          className="main-header-converge-cell"
                        >
                          <Typography fontWeight={600} fontSize="14px">
                            {getTypeLabel(type)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {row}
                    </Fragment>
                  );
                }

                return <Fragment key={`row-${uuid}`}>{row}</Fragment>;
              })
            )}

            {primaryEndorsementsForExcess.length > 0 && (
              <>
                <TableRow className="main-header-converge-tables-tower-container ">
                  <TableCell colSpan={6} className="main-header-converge-cell">
                    <Typography fontWeight={600} fontSize="14px">
                      Endorsements from primary policy
                    </Typography>
                  </TableCell>
                </TableRow>
                {primaryEndorsementsForExcess.map((endorsement) => (
                  <TableRow key={`primary-endo-${endorsement.uuid}`}>
                    <TableCell
                      width={'120px'}
                      sx={{ paddingLeft: 4, width: 120 }}
                    >
                      <Typography fontWeight={600} fontSize="14px">
                        {endorsement.name}
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell className="coverage-table-in-tower-limit-body">
                      <Typography fontSize="14px">
                        {getEndorsementLimitDisplay(
                          endorsement,
                          formatDollarAmount
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </Stack>
    );
  }
);
